import { defineComponent } from "@digitalbranch/app";

import { ASMLexer } from "./lexer";
import { Token } from "./lexer/types";

import css from './code-editor.css'
import template from './code-editor.html'

export type Transaction = {
  type: string,
  data: string | null,
  index: number
}

export type HistoryEntry = Transaction & {
  from: number,
  to: number,
  start: number,
  end: number,
  previous: number
}

export type Selection = {
  line: number,
  start: number,
  end: number,
  from: number,
  to: number,
  previous: number,
  next: number
}

export type EditorState = {
  doc: string,
  history: Array<HistoryEntry>,
  index: number,
  selection: Selection
}

function getState(doc = ''): EditorState {
  return {
    doc,
    history: [],
    index: -1,
    selection: {
      line: 1,
      start: 0,
      end: 0,
      from: 0,
      to: 0,
      previous: 0,
      next: 0
    }
  }
}

function getDataFromDataTransfer(dataTransfer: DataTransfer) {
  const [type] = dataTransfer.types

  return dataTransfer.getData(type)
}

const OPCODES = new Set([
  'mov',
  'push',
  'pop',
  'b',
  'beq',
  'bl',
  'blt',
  'bx',
  'add',
  'mul',
  'sub',
  'cmp',
  'svc',
  'bge',
  'str',
  'ldr',
  'ldrb',
  'strb',
  'orr',
  'bic'
])

export const codeEditor = defineComponent({
  name: 'code-editor',
  props: [
    { name: 'contentEditable', type: 'boolean', default: true },
    { name: 'theme', type: 'string', default: 'dark' }
  ],
  css,
  template,
  data(): { hasFocus: boolean, y: number, travel: number, state: EditorState } {
    return {
      hasFocus: false,
      y: 0,
      travel: 0,
      state: getState()
    }
  },
  delegatesFocus: true,
  listeners: {
    async ready() {
      this.$node.setAttribute('tabindex', '0');

      this.$addEventListener(document, 'selectionchange', this.handleSelectionChange.bind(this))
      this.$addEventListener(document, 'keydown', this.handleKeyDown.bind(this))
      this.$addEventListener(window, 'mousedown', this.handleMouseDown.bind(this))
      this.$addEventListener(window, 'mouseup', this.handleMouseUp.bind(this))
    },
    beforeDestroy() {
      this.$removeEventListener(document, 'selectionchange', this.handleSelectionChange.bind(this))
      this.$removeEventListener(document, 'keydown', this.handleKeyDown.bind(this))
      this.$removeEventListener(window, 'mousedown', this.handleMouseDown.bind(this))
      this.$removeEventListener(window, 'mouseup', this.handleMouseUp.bind(this))
    }
  },
  methods: {
    onSlotChange() {
      this.highlight()
    },
    handleMouseMove(e: MouseEvent) {
      const { y } = e

      this.travel-= this.y === 0 ? 0 : this.y - y
      this.y = e.y
    },
    handleMouseDown() {
      if (!this.hasFocus) return

      this.travel = 0
      this.y = 0

      this.$addEventListener(window, 'mousemove', this.handleMouseMove.bind(this))
    },
    handleMouseUp() {
      this.$removeEventListener(window, 'mousemove', this.handleMouseMove.bind(this))
    },
    highlight() {
      const lexer = new ASMLexer(this.state.doc, {
        ignoreComments: false,
        ignoreNewline: false,
        ignoreWhiteSpace: false,
        throws: false
      })
      const code = this.$refs.editor as HTMLPreElement
      const currentLineNumber = this.state.selection.line

      let result = ''
      let line = ''
      let prevToken: Token | null = null
      let lineNumber = 1
      let start = 0

      for (const token of lexer) {
        const { type, value } = token

        lexer.ignoreWhiteSpace = true

        const nextToken = lexer.peek()

        lexer.ignoreWhiteSpace = false

        switch (type) {
          case 'newline': {
            result += `<div class="line${lineNumber === currentLineNumber ? ' active' : ''}" data-line="${lineNumber}">${line || `<span data-start="${start}"><br/></span>`}</div>`
            line = ''
            lineNumber++
            break
          }
          case 'dot': {
            if (nextToken && nextToken.type === 'symbol') {
              line += `<span class="directive" data-start="${start}">${value}</span>`
            } else {
              line += `<span class="${type}" data-start="${start}">${value}</span>`
            }

            break
          }
          case 'symbol': {
            if (value.toLowerCase() === 'r' && nextToken.type === 'number') {
              line += `<span class="register" data-start="${start}">${value}${nextToken.value}</span>`
              start += nextToken.value.length

              lexer.advance()
            } else if(OPCODES.has(value.toLowerCase())) {
              line += `<span class="opcode" data-start="${start}">${value}</span>`
            } else if (prevToken && prevToken.type === 'dot') {
              line += `<span class="directive" data-start="${start}">${value}</span>`
            } else if (nextToken && nextToken.type === 'colon') {
              line += `<span class="label" data-start="${start}">${value}</span>`
            } else if (nextToken && nextToken.type === 'assign') {
              line += `<span class="var" data-start="${start}">${value}</span>`
            } else {
              line += `<span class="${type}" data-start="${start}">${value}</span>`
            }

            break
          }
          case 'number': {
            line += `<span class="${type}" data-start="${start}">${value}</span>`

            break
          }
          case 'pound': {
            let name

            switch(nextToken.type) {
              case 'number': {
                name = "immediate_value"
                break;
              }
              case 'character_constant': {
                name = "immediate_value_character_constant"
                break;
              }
              default: {
                name = type
              }
            }

            line += `<span class="${name}" data-start="${start}">${value}</span>`

            break
          }
          case 'assign':
          case 'plus':
          case 'min':
          case 'mul':
          case 'div': {
            line += `<span class="operator" data-start="${start}">${value}</span>`

            break
          }
          case 'character_constant': {
            const quote = value[0]

            line += `<span class="quote" data-start="${start}">${quote}</span><span class="${type}" data-start="${start + 1}">${value.slice(
              1,
              value.length - 1
            )}</span><span class="quote" data-start="${start + 2}">${quote}</span>`


            break
          }
          case 'string_literal': {
            const quote = value[0]

            line += `<span class="quote" data-start="${start}">${quote}</span><span class="${type}" data-start="${start + 1}">${value.slice(
              1,
              value.length - 1
            )}</span><span class="quote" data-start="${start + 2}">${quote}</span>`


            break
          }
          case 'literal': {
            line += `<span class="${type}" data-start="${start}">${value}</span>`
          }
          default: {
            line += `<span class="${type}" data-start="${start}">${value}</span>`
          }
        }

        if (type !== 'whitespace') prevToken = token

        start += value.length
      }

      if (line) result += `<div class="line">${line}</div>`

      code.innerHTML = result
    },
    handleFocus() {
      this.hasFocus = true
    },
    handleFocusOut() {
      this.hasFocus = false
    },
    handleKeyDown(e: KeyboardEvent) {
      const { key, metaKey, shiftKey } = e

      if ((key === 'ArrowUp' || key === 'ArrowDown') && shiftKey) {
        if (key === 'ArrowUp' && shiftKey) {
          this.travel--
        }

        else if (key === 'ArrowDown' && shiftKey) {
          this.travel++
        }
      } else {
        this.travel = 0

        if (key === 'z' && metaKey) {
          e.preventDefault()

          this.undo()
        }

        else if (key === 'a' && metaKey) {
          e.preventDefault()

          this.selectAll()
        }
      }
    },
    handleSelectionChange() {
      const selection = this.getSelection()

      if(selection) this.state.selection = selection

    },
    handleBeforeinput(e: InputEvent) {
      e.preventDefault()

      this.handleSelectionChange()

      const { inputType: type, data, dataTransfer } = e
      const { state: { selection: { to, end } } } = this
      const index = end + to

      this.dispatchTransaction({
        type,
        data: dataTransfer ? getDataFromDataTransfer(dataTransfer) : data,
        index
      })
    },
    getSelection() {
      if (!this.hasFocus) this.state.selection

      const selection = window.getSelection()

      if (!selection) return

      const [staticRange] = selection.getComposedRanges({ shadowRoots: [this.$node.shadowRoot!] })

      if (!staticRange) return

      const { startOffset: from, endOffset: to, startContainer, endContainer } = staticRange

      let { parentElement: startElement } = startContainer
      let { parentElement: endElement } = endContainer

      if (startContainer.nodeName === 'SPAN') startElement = startContainer as HTMLSpanElement
      else if(startContainer.nodeName === 'DIV') startElement = (startContainer as HTMLDivElement).firstElementChild as HTMLSpanElement

      if (endContainer.nodeName === 'SPAN') endElement = endContainer as HTMLSpanElement
      else if(endContainer.nodeName === 'DIV') endElement = (endContainer as HTMLDivElement).firstElementChild as HTMLSpanElement

      if (!startElement || !endElement) return

      const { parentElement: lineStart } = startElement
      const { parentElement: lineEnd } = endElement

      if (!lineStart || !lineEnd) return

      const start = parseInt(startElement.dataset.start ?? '0', 10)
      const end = parseInt(endElement.dataset.start ?? '0', 10)
      const previous = parseInt(((startElement.previousElementSibling || lineStart.previousElementSibling?.lastElementChild) as HTMLElement | null)?.dataset.start ?? '0', 10)
      const next = parseInt(((startElement.nextElementSibling || lineStart.nextElementSibling?.firstElementChild) as HTMLElement | null)?.dataset.start ?? '0', 10)
      const activeLine = this.travel > 0 ? lineEnd : lineStart
      const line = parseInt(activeLine.dataset.line ?? '0', 10)
      const currentActiveLine = activeLine.parentElement?.querySelector('.active')

      if (currentActiveLine !== activeLine) {
        if (currentActiveLine) currentActiveLine.classList.toggle('active')

        activeLine.classList.toggle('active')
      }

      return { from, to, start, end, previous, next, line }
    },
    setCaret(position: number, start: number, previousStart: number) {
      const editor = this.$refs.editor

      if (!editor) return

      const selection = window.getSelection()

      if (!selection) return

      let startContainer: HTMLElement | null = editor.querySelector(`[data-start="${start}"]`)

      if (!startContainer || position === -1) {
        startContainer = editor.querySelector(`[data-start="${previousStart}"]`)
      }

      if (!startContainer) return

      let node = startContainer.firstChild

      if (!node) return

      let length = node.textContent?.length ?? 0
      let offset

      if (length < position) {
        let characterCount = length

        while(true) {
          startContainer = (startContainer?.nextElementSibling ?? null) as HTMLElement | null

          if (!startContainer) return

          node = startContainer.firstChild ?? null

          if (!node) return

          length = node.textContent!.length

          characterCount += length

          if (characterCount >= position) break
        }
        const startOffset = parseInt(startContainer.dataset.start ?? '0', 10)

        offset = Math.max(position - startOffset, 1)
      } else if (position === -1 || position === 0) {
        offset = length
      } else {
        offset = position
      }

      const range = document.createRange()

      range.setStart(node, offset)
      range.collapse(true)

      selection.removeAllRanges()
      selection.addRange(range)
    },
    selectAll() {
      const selection = window.getSelection()

      if (!selection) return

      const range = document.createRange()
      const editor = this.$refs.editor as HTMLPreElement
      const startLine = editor.firstElementChild
      const endLine = editor.lastElementChild

      if (!startLine || !endLine) return

      const startNode = startLine.firstElementChild?.firstChild
      const endNode = endLine.lastElementChild?.lastChild

      if (!startNode || !endNode) return

      const from = 0
      const to = endNode.textContent!.length

      range.setStart(startNode, from)
      range.setEnd(endNode, to)

      selection.removeAllRanges()
      selection.addRange(range)

      this.handleSelectionChange()
    },
    updateEditor(position: number, start: number, previousStart: number) {
      this.highlight()
      this.setCaret(position, start, previousStart)
    },
    undo() {
      const { state } = this
      const { history, index: historyIndex, doc } = state

      if (historyIndex === -1) return

      const { type, data, index, from, to, end, start, previous } = history[state.index--]

      switch(type) {
        case 'insertText': {
          if (data) {
            if (from !== to) {
              const startIndex = index - data.length

              state.doc = doc.substring(0, startIndex) + data + doc.substring(startIndex + 1)
            } else {
              state.doc = doc.substring(0, index) + doc.substring(index + data.length)
            }

            this.updateEditor(Math.max(from ,to), end, previous)
          }

          break;
        }
        case 'insertParagraph': {
          state.doc = doc.substring(0, index) + doc.substring(index + 1)

          this.updateEditor(Math.max(from ,to), start, previous)

          break
        }
        case 'insertFromPaste': {
          if (data) {
            state.doc = doc.substring(0, index) + doc.substring(index + data.length)

            this.updateEditor(Math.max(from ,to), start, previous)
          }

          break;
        }
        case 'deleteByCut':
        case 'deleteContentBackward': {
          if (data) {
            const startIndex = index - data.length

            state.doc = doc.substring(0, startIndex) + data + doc.substring(startIndex)

            this.updateEditor(Math.max(from ,to), end, previous)
          }

          break;
        }
      }
    },
    dispatchTransaction(transaction: Transaction) {
      const { type, data, index } = transaction
      const { state } = this
      const { doc, history, selection: { from, to, start, end, previous } } = state

      switch(type) {
        case 'insertText': {
          if (data) {
            const range = (end + to) - (start + from)
            const position = Math.min(from, to)

            state.doc = doc.substring(0, start + from) + data + doc.substring(index)

            if (range > 0) {
              transaction.data = doc.substring(start + position, end + position + range)
            }

            this.updateEditor(from + data.length, start, previous)
          }

          break;
        }
        case 'insertParagraph': {
          state.doc = doc.substring(0, index) + '\n' + doc.substring(index)

          this.updateEditor(0, index + 1, previous)

          break
        }
        case 'insertFromPaste': {
          if (data) {
            state.doc = doc.substring(0, index) + data + doc.substring(index)

            this.updateEditor(from + data.length, start, previous)
          }

          break;
        }
        case 'deleteByCut':
        case 'deleteContentBackward': {
          const range = (end + to) - (start + from)

          let position: number;

          if (range > 0) {
            position = Math.min(to, from)

            state.doc = doc.substring(0, start + position) + doc.substring(start + position + range)

            transaction.data = doc.substring(start + position, start + position + range)
          } else {
            position = from - 1

            state.doc = doc.substring(0, start + position) + doc.substring(index)

            transaction.data = doc.substring(start + position, index)
          }

          this.updateEditor(position, start, previous)

          break;
        }
      }

      history.push({...transaction, from, to, start, end, previous})

      this.state.index = history.length - 1
    },
    setDoc(doc: string) {
      this.state = getState(doc)

      this.highlight()
    },
    getDoc() {
      return this.state.doc
    },
    getState() {
      return structuredClone(this.$$data.state)
    },
    setState(state: EditorState) {
      this.state = state

      this.highlight()
    }
  }
})